import { r, s } from '@/theme/tokens';
import { AdapterDayjs, LocalizationProvider, MuiDateTimePicker } from './datePickers';
import { FormField } from './FormField';
import {
  resolveFormFieldBackgroundColor,
  type FormFieldBackgroundColor,
  type FormFieldStyleVariant,
} from './formFieldVariants';
import type { SxProps, Theme } from './types';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';
import 'dayjs/locale/en-gb';

export type DateTimePickerProps = {
  label?: ReactNode;
  inlineLabel?: boolean;
  helpText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  minDateTime?: Dayjs;
  maxDateTime?: Dayjs;
  disabled?: boolean;
  fieldStyle?: FormFieldStyleVariant;
  inputBackgroundColor?: FormFieldBackgroundColor;
  placeholder?: string;
  textFieldSx?: SxProps<Theme>;
  openPickerButtonSx?: SxProps<Theme>;
  calendarPaperSx?: SxProps<Theme>;
  locale?: string;
  format?: string;
};

export const DateTimePicker = ({
  label,
  inlineLabel = false,
  helpText,
  errorText,
  required,
  value,
  onChange,
  minDateTime,
  maxDateTime,
  disabled,
  fieldStyle = 'default',
  inputBackgroundColor,
  placeholder,
  textFieldSx,
  openPickerButtonSx,
  calendarPaperSx,
  locale,
  format,
}: DateTimePickerProps): JSX.Element => {
  const resolvedInputBackgroundColor = resolveFormFieldBackgroundColor(
    fieldStyle,
    inputBackgroundColor,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale ?? 'en-gb'}>
      <FormField
        label={inlineLabel ? undefined : label}
        helpText={helpText}
        errorText={errorText}
        required={required}
      >
        {(props) => {
          const { label: _label, labelId: _labelId, ...fieldProps } = props;
          return (
            <MuiDateTimePicker
              value={value}
              onChange={onChange}
              minDateTime={minDateTime}
              maxDateTime={maxDateTime}
              disabled={disabled}
              ampm={false}
              format={format ?? 'DD/MM/YYYY HH:mm'}
              slotProps={{
                textField: {
                  ...fieldProps,
                  fullWidth: true,
                  required,
                  error: Boolean(errorText),
                  size: 'small',
                  label: inlineLabel ? label : undefined,
                  helperText: undefined,
                  placeholder,
                  InputLabelProps: inlineLabel ? { shrink: true } : undefined,
                  inputProps: {
                    ...(fieldProps as { inputProps?: Record<string, unknown> }).inputProps,
                    placeholder,
                  },
                  sx: [
                    {
                      '& .MuiPickersInputBase-root': {
                        backgroundColor: resolvedInputBackgroundColor,
                        paddingLeft: '12px',
                        paddingRight: '12px',
                      },
                      '& .MuiPickersOutlinedInput-root': {
                        backgroundColor: resolvedInputBackgroundColor,
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        borderRadius: r('sm'),
                      },
                      '& .MuiPickersOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                        borderRadius: r('sm'),
                      },
                      '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline':
                        {
                          borderColor: 'primary.main',
                        },
                      '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline':
                        {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                      '& .MuiPickersInputBase-sectionsContainer': {
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                      },
                      '& .MuiPickersSectionList-root': {
                        paddingLeft: '0 !important',
                        paddingRight: '0 !important',
                      },
                      '& .MuiPickersInputBase-input': {
                        paddingLeft: s('none'),
                        paddingRight: s('none'),
                        fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
                        lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight,
                      },
                      '& .MuiPickersInputBase-sectionContent': {
                        fontSize: (theme: Theme) => theme.typography.subtitle1.fontSize,
                        lineHeight: (theme: Theme) => theme.typography.subtitle1.lineHeight,
                      },
                    },
                    ...(textFieldSx
                      ? Array.isArray(textFieldSx)
                        ? textFieldSx
                        : [textFieldSx]
                      : []),
                  ],
                },
                openPickerButton: {
                  size: 'small',
                  sx: [
                    {
                      pl: 0,
                      ml: `-${s('sm')}`,
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        mr: s('xs'),
                      },
                    },
                    ...(openPickerButtonSx
                      ? Array.isArray(openPickerButtonSx)
                        ? openPickerButtonSx
                        : [openPickerButtonSx]
                      : []),
                  ],
                },
                desktopPaper: {
                  sx: [
                    {
                      backgroundColor: 'var(--floating-control-surface)',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: `${r('md')} !important`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
                mobilePaper: {
                  sx: [
                    {
                      backgroundColor: 'var(--floating-control-surface)',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: `${r('md')} !important`,
                      boxShadow: 'none',
                      overflow: 'hidden',
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
                popper: {
                  placement: 'bottom-end',
                  sx: [
                    {
                      '& .MuiPaper-root': {
                        backgroundColor: 'var(--floating-control-surface)',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: `${r('md')} !important`,
                        boxShadow: 'none',
                        overflow: 'hidden',
                      },
                      '& .MuiPickersLayout-root': {
                        backgroundColor: 'var(--floating-control-surface)',
                      },
                      '& .MuiDayCalendar-root, & .MuiPickersCalendarHeader-root, & .MuiPickersArrowSwitcher-root':
                        {
                          backgroundColor: 'var(--floating-control-surface)',
                        },
                    },
                    ...(calendarPaperSx
                      ? Array.isArray(calendarPaperSx)
                        ? calendarPaperSx
                        : [calendarPaperSx]
                      : []),
                  ],
                },
              }}
            />
          );
        }}
      </FormField>
    </LocalizationProvider>
  );
};
